import React, { useState, useEffect, useCallback } from 'react';
import {
  FiDollarSign, FiPlus, FiTrash2, FiRefreshCw, FiExternalLink,
  FiAlertCircle, FiCheckCircle,
} from 'react-icons/fi';
import { getAuthHeaders } from '../utils/auth';
import { getApiEndpoint } from '../utils/api';
import './Funding.css';

const Funding = ({ onStartGrantAgent }) => {
  const [sources, setSources] = useState([]);
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSource, setShowAddSource] = useState(false);
  const [showAddOpp, setShowAddOpp] = useState(false);
  const [checkingSource, setCheckingSource] = useState(null);
  const [newSource, setNewSource] = useState({ name: '', url: '', agency: '', notes: '', check_cadence: 'weekly' });
  const [newOpp, setNewOpp] = useState({ title: '', agency: '', url: '', deadline: '', description: '', fit_notes: '', source_type: 'manual' });
  const [message, setMessage] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [srcRes, oppRes] = await Promise.all([
        fetch(getApiEndpoint('/api/collections/funding/sources'), { headers: getAuthHeaders() }),
        fetch(getApiEndpoint('/api/collections/funding/opportunities'), { headers: getAuthHeaders() }),
      ]);
      if (srcRes.ok) setSources(await srcRes.json());
      if (oppRes.ok) setOpps(await oppRes.json());
    } catch (_) {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addSource = async () => {
    if (!newSource.name) return;
    try {
      const res = await fetch(getApiEndpoint('/api/collections/funding/sources'), {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(newSource),
      });
      if (res.ok) {
        setShowAddSource(false);
        setNewSource({ name: '', url: '', agency: '', notes: '' });
        fetchData();
      }
    } catch (_) {}
  };

  const checkSource = async (sourceId) => {
    setCheckingSource(sourceId);
    setMessage(null);
    try {
      const res = await fetch(getApiEndpoint(`/api/collections/funding/sources/${sourceId}/check`), {
        method: 'POST', headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setMessage({ type: 'success', text: `Found ${data.new_opportunities} new opportunities` });
        fetchData();
      }
    } catch (_) {}
    setCheckingSource(null);
  };

  const deleteSource = async (sourceId) => {
    await fetch(getApiEndpoint(`/api/collections/funding/sources/${sourceId}`), {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    fetchData();
  };

  const addOpp = async () => {
    if (!newOpp.title) return;
    try {
      const res = await fetch(getApiEndpoint('/api/collections/funding/opportunities'), {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(newOpp),
      });
      if (res.ok) {
        setShowAddOpp(false);
        setNewOpp({ title: '', agency: '', url: '', deadline: '', description: '', fit_notes: '', source_type: 'manual' });
        fetchData();
      }
    } catch (_) {}
  };

  const updateStatus = async (oppId, status) => {
    await fetch(getApiEndpoint(`/api/collections/funding/opportunities/${oppId}`), {
      method: 'PATCH',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    fetchData();
  };

  const deleteOpp = async (oppId) => {
    await fetch(getApiEndpoint(`/api/collections/funding/opportunities/${oppId}`), {
      method: 'DELETE', headers: getAuthHeaders(),
    });
    fetchData();
  };

  const startProposal = async (opp) => {
    try {
      const res = await fetch(getApiEndpoint('/api/grant-proposals/from-nofo'), {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: opp.title,
          nofo_number: opp.number || '',
          idea_title: '',
          synopsis: opp.description || opp.fit_notes || '',
          nofo_url: opp.url || '',
        }),
      });
      if (!res.ok) throw new Error('Failed');
      await updateStatus(opp.id, 'applying');
      const proposal = await res.json();
      onStartGrantAgent && onStartGrantAgent(null, null, proposal.id);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  if (loading) {
    return <div className="fd-loading"><div className="spinner" /><p>Loading funding data...</p></div>;
  }

  const activeOpps = opps.filter(o => o.status !== 'dismissed');
  const dismissedOpps = opps.filter(o => o.status === 'dismissed');

  return (
    <div className="fd-page">

      {message && (
        <div className={`fd-message fd-message--${message.type}`}>
          {message.type === 'success' ? <FiCheckCircle /> : <FiAlertCircle />}
          {message.text}
        </div>
      )}

      {/* Header */}
      <div className="fd-header">
        <div>
          <h2><FiDollarSign /> Funding Opportunities</h2>
          <p className="fd-subtitle">Track program pages you monitor and manage your funding pipeline</p>
        </div>
      </div>

      <div className="fd-layout">

        {/* Left: Watched Programs */}
        <div className="fd-section">
          <div className="fd-section-header">
            <h3>Watched Programs</h3>
            <button className="fd-btn fd-btn--primary" onClick={() => setShowAddSource(true)}>
              <FiPlus /> Add Program
            </button>
          </div>
          <p className="fd-hint">Add the funding program pages you check regularly — NSF solicitations, FHWA research, TRB NCHRP, etc.</p>

          {showAddSource && (
            <div className="fd-add-form">
              <input placeholder="Program name (e.g., NSF CMMI - SAI)" value={newSource.name}
                onChange={e => setNewSource(p => ({ ...p, name: e.target.value }))} />
              <input placeholder="Program page URL" value={newSource.url}
                onChange={e => setNewSource(p => ({ ...p, url: e.target.value }))} />
              <div className="fd-form-row">
                <input placeholder="Agency (e.g., NSF)" value={newSource.agency}
                  onChange={e => setNewSource(p => ({ ...p, agency: e.target.value }))} />
                <input placeholder="Program (e.g., CMMI)" value={newSource.program}
                  onChange={e => setNewSource(p => ({ ...p, program: e.target.value }))} />
              </div>
              <div className="fd-form-row">
                <select value={newSource.check_cadence} onChange={e => setNewSource(p => ({ ...p, check_cadence: e.target.value }))}>
                  <option value="daily">Check daily</option>
                  <option value="weekly">Check weekly</option>
                  <option value="monthly">Check monthly</option>
                </select>
                <input placeholder="Notes" value={newSource.notes}
                  onChange={e => setNewSource(p => ({ ...p, notes: e.target.value }))} />
              </div>
              <div className="fd-form-actions">
                <button className="fd-btn fd-btn--primary" onClick={addSource} disabled={!newSource.name}>Save</button>
                <button className="fd-btn fd-btn--outline" onClick={() => setShowAddSource(false)}>Cancel</button>
              </div>
            </div>
          )}

          {sources.length === 0 && !showAddSource && (
            <div className="fd-empty">No programs watched yet. Add the pages you check for funding.</div>
          )}

          {sources.map(src => (
            <div key={src.id} className="fd-source-card">
              <div className="fd-source-top">
                <strong>{src.name}</strong>
                {src.agency && <span className="fd-agency-badge">{src.agency}</span>}
                {src.url && (
                  <a href={src.url} target="_blank" rel="noreferrer" className="fd-link"><FiExternalLink /></a>
                )}
              </div>
              {src.notes && <p className="fd-source-notes">{src.notes}</p>}
              <div className="fd-source-actions">
                <button onClick={() => checkSource(src.id)} disabled={checkingSource === src.id}>
                  <FiRefreshCw /> {checkingSource === src.id ? 'Checking...' : 'Check Now'}
                </button>
                <span className="fd-muted">
                  {src.last_checked_at ? `Checked ${new Date(src.last_checked_at).toLocaleDateString()}` : 'Never checked'}
                  {' · '}{src.check_cadence || 'weekly'}
                  {src.email_notifications ? ' · email on' : ''}
                </span>
                <button className="fd-delete-btn" onClick={() => deleteSource(src.id)}><FiTrash2 /></button>
              </div>
            </div>
          ))}
        </div>

        {/* Right: Opportunities */}
        <div className="fd-section">
          <div className="fd-section-header">
            <h3>Opportunities</h3>
            <button className="fd-btn fd-btn--outline" onClick={() => setShowAddOpp(true)}>
              <FiPlus /> Add Manually
            </button>
          </div>

          {showAddOpp && (
            <div className="fd-add-form">
              <input placeholder="Opportunity title" value={newOpp.title}
                onChange={e => setNewOpp(p => ({ ...p, title: e.target.value }))} />
              <div className="fd-form-row">
                <input placeholder="Agency" value={newOpp.agency}
                  onChange={e => setNewOpp(p => ({ ...p, agency: e.target.value }))} />
                <input type="date" placeholder="Deadline" value={newOpp.deadline}
                  onChange={e => setNewOpp(p => ({ ...p, deadline: e.target.value }))} />
              </div>
              <input placeholder="URL" value={newOpp.url}
                onChange={e => setNewOpp(p => ({ ...p, url: e.target.value }))} />
              <textarea placeholder="Why this fits your research" rows={2} value={newOpp.fit_notes}
                onChange={e => setNewOpp(p => ({ ...p, fit_notes: e.target.value }))} />
              <select value={newOpp.source_type} onChange={e => setNewOpp(p => ({ ...p, source_type: e.target.value }))}>
                <option value="manual">Found it myself</option>
                <option value="colleague">Colleague tip</option>
              </select>
              <div className="fd-form-actions">
                <button className="fd-btn fd-btn--primary" onClick={addOpp} disabled={!newOpp.title}>Save</button>
                <button className="fd-btn fd-btn--outline" onClick={() => setShowAddOpp(false)}>Cancel</button>
              </div>
            </div>
          )}

          {activeOpps.length === 0 && !showAddOpp && (
            <div className="fd-empty">No opportunities tracked yet. Check your watched programs or add one manually.</div>
          )}

          {activeOpps.map(opp => {
            const daysLeft = opp.deadline ? Math.ceil((new Date(opp.deadline) - Date.now()) / 86400000) : null;
            return (
              <div key={opp.id} className={`fd-opp-card fd-opp--${opp.status}`}>
                <div className="fd-opp-top">
                  {opp.agency && <span className="fd-agency-badge">{opp.agency}</span>}
                  {opp.number && <span className="fd-opp-number">{opp.number}</span>}
                  {daysLeft !== null && (
                    <span className={`fd-deadline ${daysLeft < 0 ? 'fd-deadline--overdue' : daysLeft < 30 ? 'fd-deadline--soon' : ''}`}>
                      {daysLeft < 0 ? `${Math.abs(daysLeft)}d overdue` : daysLeft === 0 ? 'Today' : `${daysLeft}d left`}
                    </span>
                  )}
                  <span className={`fd-status fd-status--${opp.status}`}>{opp.status}</span>
                </div>
                <div className="fd-opp-title">
                  {opp.url ? <a href={opp.url} target="_blank" rel="noreferrer">{opp.title}</a> : opp.title}
                </div>
                {opp.description && <p className="fd-opp-desc">{opp.description}</p>}
                {opp.fit_notes && <p className="fd-opp-fit">{opp.fit_notes}</p>}
                <div className="fd-opp-actions">
                  {opp.status === 'discovered' && (
                    <button onClick={() => updateStatus(opp.id, 'interested')}>Interested</button>
                  )}
                  {(opp.status === 'discovered' || opp.status === 'interested') && (
                    <button className="fd-btn fd-btn--primary fd-btn--sm" onClick={() => startProposal(opp)}>
                      Start Draft &rarr;
                    </button>
                  )}
                  {opp.status === 'applying' && (
                    <button onClick={() => updateStatus(opp.id, 'applied')}>Mark Applied</button>
                  )}
                  {opp.status !== 'dismissed' && (
                    <button onClick={() => updateStatus(opp.id, 'dismissed')}>Dismiss</button>
                  )}
                  <button className="fd-delete-btn" onClick={() => deleteOpp(opp.id)}><FiTrash2 /></button>
                </div>
                {opp.source_type === 'colleague' && <span className="fd-source-type-badge">Colleague tip</span>}
              </div>
            );
          })}

          {dismissedOpps.length > 0 && (
            <details className="fd-dismissed">
              <summary>{dismissedOpps.length} dismissed</summary>
              {dismissedOpps.map(opp => (
                <div key={opp.id} className="fd-opp-card fd-opp--dismissed">
                  <span className="fd-opp-title-sm">{opp.title}</span>
                  <button className="fd-delete-btn" onClick={() => deleteOpp(opp.id)}><FiTrash2 /></button>
                </div>
              ))}
            </details>
          )}
        </div>
      </div>
    </div>
  );
};

export default Funding;
